import React, { createContext, useContext, useEffect, useState } from 'react';
import { Node } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { Plugin, Selection, TextSelection } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
import { useTranslation } from 'react-i18next';
import { RecordedMediaPlayer } from '../../../components/RecordedMedia';
import CompactLoaderMark from '../../../components/Brand/CompactLoaderMark';
import { RedoIcon } from '../../../components/Brand/Icon';
import { isConsentRequired } from '../../../legal/requiredNotice';

export const MEDIA_SYNC = 'wiw-media-sync';
export const MEDIA_DELETE = 'wiw-media-delete';
export const MediaDraftContext = createContext(null);
export const recordedMediaKeys = doc => {
  const keys = [];
  doc?.descendants(node => { if (node.type.name === 'recordedMedia') keys.push(node.attrs.mediaKey); });
  return keys;
};

function removedImages(before, transaction) {
  const images = doc => {
    const result = [];
    doc.descendants((node, pos) => { if (node.type.name === 'image') result.push({ node, pos }); });
    return result;
  };
  const remaining = images(transaction.doc);
  const unmatched = images(before).filter(image => {
    const mapped = transaction.mapping.mapResult(image.pos);
    const index = remaining.findIndex(next => !mapped.deleted && next.pos === mapped.pos && next.node.eq(image.node));
    if (index < 0) return true;
    remaining.splice(index, 1);
    return false;
  });
  // setContent/Undo may replace the document while retaining images. Count
  // those too, without confusing duplicate copies of the same image URL.
  return unmatched.filter(image => {
    const index = remaining.findIndex(next => next.node.eq(image.node));
    if (index < 0) return true;
    remaining.splice(index, 1);
    return false;
  });
}

// Draft HTML keeps stable local keys; only accepted server IDs leave the editor.
// The server independently verifies them against this opinion's attachments.
export function serializeRecordedMedia(html, items) {
  const dom = document.createElement('div');
  dom.innerHTML = html;
  const used = new Set();
  dom.querySelectorAll('span[data-recorded-media-id]').forEach(node => {
    const key = node.getAttribute('data-recorded-media-id');
    const item = items.find(value => ['audio', 'video'].includes(value.kind) && (value.localId === key || value.id === key));
    if (!item || used.has(item.id)) { node.remove(); return; }
    if (!item.id || !['ready', 'rejected'].includes(item.status) || (item.error && item.status !== 'rejected')) throw new Error('MEDIA_NOT_READY');
    used.add(item.id);
    const spaced = node.getAttribute('data-wiw-censored-spaced') === 'true';
    for (const attribute of Array.from(node.attributes)) node.removeAttribute(attribute.name);
    node.setAttribute('data-recorded-media-id', item.id);
    if (spaced && item.status === 'rejected') node.setAttribute('data-wiw-censored-spaced', 'true');
    node.textContent = '';
  });
  return dom.innerHTML;
}

export function RecordingView({ node, editor, getPos }) {
  const { t } = useTranslation();
  const context = useContext(MediaDraftContext);
  const item = context?.media.items.find(value => ['audio', 'video'].includes(value.kind)
    && (value.localId === node.attrs.mediaKey || value.id === node.attrs.mediaKey));
  const [preview, setPreview] = useState(null);
  const blob = item?.status === 'rejected' ? null : item?.blob;
  useEffect(() => {
    if (!blob) { setPreview(null); return undefined; }
    const url = URL.createObjectURL(blob);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);
  const busy = item && context.media.uploading(item);
  const consentRequired = isConsentRequired(item?.error);
  const error = item?.error && !consentRequired;
  const retry = item && !consentRequired && !busy && !['ready', 'rejected'].includes(item.status)
    && (item.error || !item.autoUpload || ['failed', 'cancelled'].includes(item.status));
  const unavailable = item && !consentRequired && item.status !== 'ready' && !item.id && !context.caps[item.kind]?.enabled;
  const kind = item?.kind || 'audio';
  const removing = context?.removingKeys?.includes(node.attrs.mediaKey);
  const focusAfter = event => {
    // Only the empty right gutter handles this gesture. Playback controls keep
    // their own pointer behavior and selecting a recording remains possible.
    if (event.target !== event.currentTarget || !editor.isEditable || editor.isDestroyed) return;
    const pos = getPos();
    if (typeof pos !== 'number') return;
    event.preventDefault();
    editor.commands.setTextSelection(pos + node.nodeSize);
    editor.view.focus();
  };
  const keepPlaybackCaret = event => {
    // Playing/pausing is not text selection. Keep the caret and keyboard in
    // place; range inputs retain their native drag/focus behavior.
    if (event.target.closest('.recorded-media') && !event.target.closest('input')) event.preventDefault();
  };
  const loadingStatus = <span className={`editor-media-node__loading${busy ? ' is-busy' : ''}`}
    role={busy ? 'status' : undefined} aria-hidden={!busy}
    aria-label={busy ? t('media:processing', { defaultValue: 'Processing recording' }) : undefined}>
    {busy && <CompactLoaderMark />}
  </span>;
  return <NodeViewWrapper as="span" contentEditable={false}
    className={`editor-media-node editor-${kind}-node${retry || error || unavailable ? ' editor-media-node--with-status' : ''}`}
    data-recorded-media-node="" data-recorded-audio-node={kind === 'audio' ? '' : undefined}
    data-kind={kind} data-status={item?.status || 'unavailable'}
    data-removing={removing ? 'true' : undefined} data-no-swipe onPointerDown={focusAfter} onMouseDownCapture={keepPlaybackCaret}>
    {item && (!blob || preview)
      ? <RecordedMediaPlayer inline media={blob && preview ? { ...item, status: 'ready' } : item} localUrl={blob ? preview : null} status={loadingStatus} processing={busy} />
      : <span className="editor-media-node__placeholder" role="status">{item ? <CompactLoaderMark />
        : t('media:failed', { defaultValue: 'Recording unavailable' })}</span>}
    {(retry || error || unavailable) && <span className="editor-media-node__status" aria-live="polite">
      {error && <span role="alert">{t(item.status === 'rejected' ? 'media:rejected' : 'media:uploadError', {
        defaultValue: item.status === 'rejected' ? 'This video could not be approved. Remove it or record again.'
          : 'Could not prepare this recording. Your draft is still here.' })}</span>}
      {unavailable && !item.error && <span>{t('media:recordingDisabled', {
        defaultValue: 'New recordings are temporarily unavailable. Your recording stays on this device.' })}</span>}
      {retry && <button type="button" aria-label={t('media:retryUpload', { defaultValue: 'Retry' })}
        disabled={context.disabled || (!item.id && !context.caps[item.kind]?.enabled) || item.error === 'MEDIA_DRAFT_LIMIT'}
        onClick={() => context.media.upload(item)}><RedoIcon /></button>}
    </span>}
  </NodeViewWrapper>;
}

const RecordedMediaExtension = Node.create({
  name: 'recordedMedia', group: 'inline', inline: true, atom: true, selectable: true,
  addOptions() { return { onDeleteRequest: () => {} }; },
  addAttributes() { return { mediaKey: { default: null }, censoredSpaced: { default: false } }; },
  parseHTML() {
    return [{ tag: 'span[data-recorded-media-id]', getAttrs: element => {
      const mediaKey = element.getAttribute('data-recorded-media-id');
      return /^[a-zA-Z0-9_-]{1,80}$/.test(mediaKey || '')
        ? { mediaKey, censoredSpaced: element.getAttribute('data-wiw-censored-spaced') === 'true' } : false;
    } }];
  },
  renderHTML({ node }) { return ['span', { 'data-recorded-media-id': node.attrs.mediaKey,
    ...(node.attrs.censoredSpaced ? { 'data-wiw-censored-spaced': 'true' } : {}) }]; },
  renderText() { return ''; },
  addCommands() {
    return {
      insertRecordedMedia: (mediaKey, { at, updateSelection = true } = {}) => ({ tr, dispatch }) => {
        const $at = tr.doc.resolve(at ?? tr.selection.to);
        const paragraph = this.editor.schema.nodes.paragraph;
        const media = this.editor.schema.nodes[this.name].create({ mediaKey });
        const inText = $at.parent.isTextblock;
        const from = inText ? $at.before() : $at.pos;
        const to = inText ? $at.after() : $at.pos;
        const before = inText && $at.parent.content.cut(0, $at.parentOffset);
        const after = inText && $at.parent.content.cut($at.parentOffset);
        const emptyLine = node => node?.type === paragraph && node.content.size === 0;
        const nodes = [];
        if (before?.size) nodes.push($at.parent.copy(before));
        // Reuse an existing blank neighbour; successive recordings share one
        // editable line. Never replenish lines after the user removes them.
        if (before?.size || !emptyLine(tr.doc.resolve(from).nodeBefore)) nodes.push(paragraph.create());
        nodes.push(paragraph.create(null, media));
        const below = from + nodes.reduce((size, node) => size + node.nodeSize, 0) + 1;
        if (after?.size || !emptyLine(tr.doc.resolve(to).nodeAfter)) nodes.push(paragraph.create());
        if (after?.size) nodes.push($at.parent.copy(after));
        if (dispatch) {
          tr.replaceWith(from, to, nodes).setMeta(MEDIA_SYNC, true);
          if (updateSelection) tr.setSelection(TextSelection.create(tr.doc, below)).scrollIntoView();
        }
        return true;
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(RecordingView, {
      // Tiptap's default only recognizes the button itself, not the SVG/path
      // inside it. Those taps used to select the whole non-editable atom.
      stopEvent: ({ event }) => Boolean(event.target?.closest?.('.recorded-media, .editor-media-node__status')),
    });
  },
  addProseMirrorPlugins() {
    const editor = this.editor;
    const request = this.options.onDeleteRequest;
    let pending = null;
    let destroyed = false;
    return [new Plugin({
      appendTransaction: (_transactions, _oldState, state) => {
        const selection = state.selection;
        if (!editor.isEditable || editor.view.composing || pending
          || !(selection instanceof TextSelection) || !selection.empty) return null;
        const { $from } = selection;
        if ($from.nodeAfter?.type.name !== 'recordedMedia' || $from.nodeBefore?.type.name === 'recordedMedia') return null;
        // Pointer, Home and native mobile selections all obey the same rule.
        // Keep ranges and node selections intact for copy/delete, and allow the
        // position between adjacent recordings (the right of the previous one).
        return state.tr.setSelection(TextSelection.create(state.doc, $from.pos + $from.nodeAfter.nodeSize))
          .setMeta('addToHistory', false);
      },
      props: {
        decorations: state => pending && state.doc.eq(pending.before)
          ? DecorationSet.create(state.doc, pending.images.map(({ node, pos }) =>
            Decoration.node(pos, pos + node.nodeSize, { 'data-removing-image': 'true' })))
          : null,
        handleClick: (view, pos) => {
          if (!view.editable || view.composing || pending) return false;
          const $pos = view.state.doc.resolve(pos);
          if ($pos.parent.type.name !== 'paragraph' || $pos.parent.content.size) return false;
          const previous = view.state.doc.resolve($pos.before()).nodeBefore?.lastChild;
          const next = view.state.doc.resolve($pos.after()).nodeAfter?.firstChild;
          if (previous?.type.name !== 'recordedMedia' && next?.type.name !== 'recordedMedia') return false;
          // After the recorder loses focus, browsers can place the native
          // caret here while ProseMirror keeps its old selection below the
          // media. Bind this explicit tap before Delete/Backspace or typing.
          view.dispatch(view.state.tr.setSelection(TextSelection.create(view.state.doc, pos)));
          view.focus();
          return true;
        },
        handleKeyDown: (view, event) => {
          if (event.key !== 'ArrowLeft' || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey
            || view.composing || !editor.isEditable || pending) return false;
          const { selection, doc } = view.state;
          if (!(selection instanceof TextSelection) || !selection.empty
            || selection.$from.nodeBefore?.type.name !== 'recordedMedia') return false;
          const before = selection.from - selection.$from.nodeBefore.nodeSize;
          const $before = doc.resolve(before);
          // Skip the unavailable left edge, without trapping keyboard users on
          // the recording when earlier text or another paragraph is editable.
          const target = $before.nodeBefore?.type.name === 'recordedMedia' ? before : before - 1;
          const previous = target >= 0 ? Selection.findFrom(doc.resolve(target), -1, true) : null;
          if (previous) view.dispatch(view.state.tr.setSelection(previous).scrollIntoView());
          return true;
        },
      },
      filterTransaction: (tr, state) => {
        if (!tr.docChanged || tr.getMeta(MEDIA_SYNC) || tr.getMeta(MEDIA_DELETE)) return true;
        if (pending) return false;
        const after = recordedMediaKeys(tr.doc);
        const removed = recordedMediaKeys(state.doc).filter(key => !after.includes(key));
        const images = removedImages(state.doc, tr);
        if (!removed.length && !images.length) return true;
        const proposal = { before: state.doc, transaction: tr, images };
        pending = proposal;
        // A DOM transaction may arrive while ProseMirror is updating. Render
        // confirmation after it finishes rejecting the destructive change.
        queueMicrotask(() => {
          if (destroyed || pending !== proposal || editor.isDestroyed) return;
          request({
            keys: removed,
            images: images.map(({ pos }) => pos),
            confirm: () => {
              if (pending !== proposal || editor.isDestroyed) return false;
              pending = null;
              if (!editor.state.doc.eq(proposal.before)) return false;
              editor.view.dispatch(proposal.transaction.setMeta(MEDIA_DELETE, true));
              return true;
            },
            cancel: () => { if (pending === proposal) pending = null; },
          });
        });
        return false;
      },
      view: view => {
        const owner = view.dom.ownerDocument;
        const syncNativeCaret = () => {
          if (!view.hasFocus() || !view.editable || view.composing || pending || destroyed) return;
          const selection = owner.getSelection();
          if (!selection?.isCollapsed || !selection.anchorNode || !view.dom.contains(selection.anchorNode)) return;
          const pos = view.posAtDOM(selection.anchorNode, selection.anchorOffset);
          if (pos < 0) return;
          const $pos = view.state.doc.resolve(pos);
          if ($pos.nodeAfter?.type.name !== 'recordedMedia' || $pos.nodeBefore?.type.name === 'recordedMedia') return;
          const after = pos + $pos.nodeAfter.nodeSize;
          view.dispatch(view.state.tr.setSelection(TextSelection.create(view.state.doc, after)));
          // The normalized model may equal the old model, so ProseMirror can
          // skip its DOM update. Synchronize the native caret explicitly.
          const target = view.domAtPos(after);
          selection.collapse(target.node, target.offset);
        };
        owner.addEventListener('selectionchange', syncNativeCaret);
        return { destroy: () => {
          destroyed = true; pending = null;
          owner.removeEventListener('selectionchange', syncNativeCaret);
        } };
      },
    })];
  },
});

export default RecordedMediaExtension;
